import React, { useEffect, useMemo } from 'react'
import {
  makeArrayFromLength,
  formatCamelCaseToSentenceCase,
} from "oolib";
import { extractAllBlocksFromTpl } from "@okf/ootils";
import { useGetQueryData } from "../../../../../../../utils/react-query-hooks/general";
import { useAggrMetricsAcrossDimensions } from '../WidgetBarChart/queries/useAggrMetricsAcrossDimensions';
import { processRawConfigFn } from './processRawConfigFn';
import { WidgetErrorState, WidgetLoadingState } from '../../WidgetRenderer/comps/altStatePlaceholders';
import { useUpdateLoadedWidgetsTracker } from '../../../ReportLandingContext/useUpdateLoadedWidgetsTracker';
import { useReportLandingContext } from '../../../ReportLandingContext';
import { PieChart } from '@/components/dataviz/PieChart';
import { PieChartConfigForm } from './ConfigForm';
import { filterByHiddenDatapoints, filterChildrenByHiddenDatapoints, buildHiddenMatcher, isHiddenDatapoint } from '../utils/filterHiddenDatapoints';
import { resolveHidden } from '../utils/visibilityState';
import { useAnnoFilterConfig, canDrill } from "../utils/annoDrilldown/useAnnoDrilldown";
import { AnnoDrilldownPanel, AnnoDrilldownCount } from "../utils/annoDrilldown/AnnoDrilldownPanel";

/**
 * cases to test for: 
 * x: tag y: tag : WORKS
 * x: main. y: tag : WORKS 
 * x: main. y: .main : WORKS
 * x: tag y: rolluptag : WORKS
 * x: tag y: rollupValuepath : WORKS
 * 
 * x: annotag y: rollupAnnoTag : WORKS
 * x: annotag y: tag : WORKS
 * x: annotag y: main. : WORKS
 * x: rollupAnnoTag y: rollupTag
 */



export const Component = ({
  contentType,
  dimensions,
  id,
  widgetIdx,
  activeFilters = [],
  actions,
  presetActiveFilters = [],
  widgetFilters = [],
  style = {},
  onClick,
  dim1Visibility,
  dim2Visibility,
  dim3Visibility,
  dim4Visibility,
  pieChartType,
  combineSegments,
  numberOfSegments,
  enableVisibilityFetching = false,
  prefetchedData,
  // The dimension option as the picker stored it, `filterKey` included.
  dimension1,
  hideZeroValues = false,
}) => {
  const allTpls = useGetQueryData(["allTpls"]);
  const { updateWidgetDataStore } = useReportLandingContext() || {};


  const getFieldLabel = (dim) => {
    let vp = typeof dim === 'string' ? dim : dim.valuePath || dim.path;
    // in case of rollup dimensions, vp will be an array. we want to display the label of the last valuePath in the array, as that is the one that is being rolled up on.
    if (Array.isArray(vp)) vp = vp[vp.length - 1];
    if (!vp) return undefined;
    const tpl = allTpls?.find(t => t.kp_content_type === contentType);
    if (!tpl) return formatCamelCaseToSentenceCase(vp.replace("main.", ""));
    const blocks = extractAllBlocksFromTpl({ tpl });
    const block = blocks.find(b => b.valuePath === vp);
    return block?.props?.shortLabel || block?.props?.label ||
      formatCamelCaseToSentenceCase(vp.replace("main.", ""));
  };

  const breakdownCategoryName = useMemo(() => {
    if (!dimensions || dimensions.length < 2 || !contentType) return undefined;
    return getFieldLabel(dimensions[1]);
  }, [dimensions, contentType, allTpls]);

  const levelNames = useMemo(() => {
    if (!dimensions?.length || !contentType) return [];
    return dimensions.map(dim => getFieldLabel(dim)).filter(Boolean);
  }, [dimensions, contentType, allTpls]);

  // A slice is always one dimension-1 bucket — a breakdown pie sums its groups
  // back up (see usePieChartData) — so only dimension1 is pinned. The
  // multi-layer donut is out: its inner levels carry no bucket ids.
  const filterConfig = useAnnoFilterConfig();
  // Never on a public snapshot: the chart replays frozen data there, and the
  // panel would fire live annotation queries for an anonymous viewer.
  const slicesAreDrillable =
    !prefetchedData &&
    pieChartType !== "multiLayer" &&
    contentType === "annotations" &&
    canDrill([dimension1], filterConfig);

  const drilldownProps = (datum) => ({
    datum,
    dims: [dimension1],
    baseFilters: [...activeFilters, ...presetActiveFilters, ...widgetFilters],
    filterConfig,
  });

  const {data: dataFromApiCall, status: statusFromApiCall, error, ref} = useAggrMetricsAcrossDimensions({
    vizCardId: id,
    contentType: contentType,
    dimensions: dimensions,
    activeFilters: [
      ...activeFilters,
      ...presetActiveFilters,
      ...widgetFilters
    ],
    metrics: [{ "type": "count", "name": "count" }],
    enableVisibilityFetching,
    queryOptions: { enabled: prefetchedData ? false : true },
    includeZeroCounts: !hideZeroValues,
  })

  const data = prefetchedData || dataFromApiCall;
  const status = prefetchedData ? "success" : statusFromApiCall;

  useEffect(() => {
    if (status === "success" && data?.length) {
      updateWidgetDataStore && updateWidgetDataStore(id, data);
    }
  }, [data, status, id, updateWidgetDataStore]);

  useUpdateLoadedWidgetsTracker({status, widgetIdx, hasData: !!data?.length, widgetId: id})


  const genViz = () => {
    // What to hide right now, per layer. In manual mode that's the stored
    // selection; in dynamic mode it's Top/Bottom-N recomputed from the CURRENT
    // data — never a list precomputed when the preset was set, which would go
    // stale the moment a filter changed the data underneath it.
    const hiddenDim1Datapoints = resolveHidden({ state: dim1Visibility, data, dimension: 1 });
    const hiddenDim2Datapoints = resolveHidden({ state: dim2Visibility, data, dimension: 2 });
    const hiddenDim3Datapoints = resolveHidden({ state: dim3Visibility, data, dimension: 3 });
    const hiddenDim4Datapoints = resolveHidden({ state: dim4Visibility, data, dimension: 4 });

    // Filter out hidden datapoints (dim1 = top level, dim2+ = nested children levels)
    let filteredData = filterByHiddenDatapoints(data, hiddenDim1Datapoints);
    filteredData = filterChildrenByHiddenDatapoints(filteredData, hiddenDim2Datapoints, { keepEmptyGroups: !hideZeroValues });
    // For dim3/dim4 (sunburst deeper levels), filter recursively.
    // Hidden lists may contain plain strings (manual mode) or {display,value}
    // objects (dynamic Top-N/Bottom-N) — use isHiddenDatapoint so both match.
    if (hiddenDim3Datapoints?.length) {
      const dim3Matcher = buildHiddenMatcher(hiddenDim3Datapoints);
      filteredData = filteredData.map(d => ({
        ...d,
        children: d.children?.map(child => ({
          ...child,
          children: (child.children || []).filter(gc => !isHiddenDatapoint(gc, dim3Matcher)),
        })),
      }));
    }
    if (hiddenDim4Datapoints?.length) {
      const dim4Matcher = buildHiddenMatcher(hiddenDim4Datapoints);
      filteredData = filteredData.map(d => ({
        ...d,
        children: d.children?.map(child => ({
          ...child,
          children: (child.children || []).map(gc => ({
            ...gc,
            children: (gc.children || []).filter(ggc => !isHiddenDatapoint(ggc, dim4Matcher)),
          })),
        })),
      }));
    }

    const getLengthOfLongestChildrenArray = (data) => {
      let longestSoFar = 0;
      for(const d of data){
        if(d.children?.length > longestSoFar){
          longestSoFar = d.children?.length
        }
      }
      return longestSoFar;
    }

    return (
      <PieChart
        key={JSON.stringify(activeFilters)}
        breakdownCategoryName={breakdownCategoryName}
        data={filteredData}
        valuePath={
          filteredData.every(d => !d.children || d.children.length === 0) //meaning data is not in broken down format. better to do this way rather than dimensions.length === 1 because, for groupquestions, dimension length will be 1, but still it will be broken down data
          ? "count"
          : makeArrayFromLength(getLengthOfLongestChildrenArray(filteredData)).map((d,i) => `children.${i}.count`)
        }
        tooltipLabelsPath={filteredData.every(d => !d.children || d.children.length === 0)
          ? undefined
          : makeArrayFromLength(getLengthOfLongestChildrenArray(filteredData)).map((d,i) => `children.${i}.display`)}
        labelPath="display"
        colorIdx={widgetIdx % 4}
        style={{ ...style, height: style.maxHeight }}
        onClick={onClick}
        renderDatumDetails={
          slicesAreDrillable ? (d) => <AnnoDrilldownPanel {...drilldownProps(d)} /> : undefined
        }
        renderDatumSubHeader={
          slicesAreDrillable ? (d) => <AnnoDrilldownCount {...drilldownProps(d)} /> : undefined
        }
        levelNames={levelNames}
        // No `?? true` here — DataPivot/Overview render this Component directly
        // and must default OFF; the report path opts in via processRawConfigFn.
        combineSegments={
          pieChartType !== 'multiLayer' && dimensions?.length === 1
            ? combineSegments
            : false
        }
        numberOfSegments={numberOfSegments}
      />
    );
  };

  return (
    <div ref={ref}>
      {status === "loading" ? (
        <WidgetLoadingState/>
      ) : status === "error" ? (
        <WidgetErrorState error={error}/>
      ) : (
        status === "success" && genViz()
      )}
    </div>
  );
};

export const optionDef = { display: "Pie Chart", icon: "ChartDonut" , value: "pieChart", comp: 'WidgetPieChart' };

export const defaultConfig = {props: {}} //undefined; // could be some default props in this format: { props: {... } }

export { processRawConfigFn };

export const ConfigForm = (props) => <PieChartConfigForm
  widgetIcon={optionDef.icon}
  widgetTitle={optionDef.display}
  {...props}
/>;

export const someReqPropsUnset = widgetConfig => {
  return !widgetConfig?.props?.dimension1
}
